import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import {
  MapPin,
  Plus,
  Edit2,
  Trash2,
  Building2,
  ChevronRight,
  FolderOpen,
  Printer,
} from 'lucide-react';
import { useLabelQueue } from '@/contexts/LabelQueueContext';
import { useToast } from '@/components/ui/Toast';

interface Toolroom {
  id: string;
  name: string;
  description?: string;
  is_main: number;
  active: number;
  location_count: number;
}

interface StorageLocation {
  id: string;
  toolroom_id: string;
  parent_id?: string;
  name: string;
  location_type: string;
  barcode?: string;
  capacity?: number;
  description?: string;
  item_count?: number;
}

export function Locations() {
  const queryClient = useQueryClient();
  const { addLabel } = useLabelQueue();
  const { addToast } = useToast();
  const [selectedToolroom, setSelectedToolroom] = useState<string | null>(null);
  const [showAddToolroomModal, setShowAddToolroomModal] = useState(false);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [editingToolroom, setEditingToolroom] = useState<Toolroom | null>(null);
  const [editingLocation, setEditingLocation] = useState<StorageLocation | null>(null);

  // Fetch toolrooms
  const { data: toolrooms, isLoading: toolroomsLoading } = useQuery({
    queryKey: ['toolrooms'],
    queryFn: async () => {
      const res = await api.get<Toolroom[]>('/locations/toolrooms');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  // Fetch locations for selected toolroom
  const { data: locations, isLoading: locationsLoading } = useQuery({
    queryKey: ['locations', selectedToolroom],
    queryFn: async () => {
      if (!selectedToolroom) return [];
      const res = await api.get<StorageLocation[]>(`/locations/toolrooms/${selectedToolroom}/locations`);
      if (res.success) return res.data;
      throw new Error(res.error);
    },
    enabled: !!selectedToolroom,
  });

  const deleteToolroomMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/locations/toolrooms/${id}`);
      if (!res.success) throw new Error(res.error || 'Failed to delete');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['toolrooms'] });
      if (selectedToolroom) setSelectedToolroom(null);
    },
  });

  const deleteLocationMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/locations/storage/${id}`);
      if (!res.success) throw new Error(res.error || 'Failed to delete');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations', selectedToolroom] });
      queryClient.invalidateQueries({ queryKey: ['toolrooms'] });
    },
  });

  // Group locations by parent
  const topLevelLocations = locations?.filter(l => !l.parent_id) || [];
  const getChildren = (parentId: string) => locations?.filter(l => l.parent_id === parentId) || [];

  // Add location to label queue
  const handlePrintLabel = (location: StorageLocation) => {
    // Generate a unique code for the location (using barcode if available, otherwise ID)
    const code = location.barcode || `L-${location.id.slice(0, 8).toUpperCase()}`;
    addLabel({
      code,
      title: location.name,
      subtitle: location.location_type,
      type: 'location',
      id: location.id,
    });
    addToast({
      type: 'info',
      title: 'Added to print queue',
      duration: 2000,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Locations</h1>
          <p className="text-foreground-muted">Manage toolrooms and storage locations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setShowAddToolroomModal(true)}>
            <Plus className="h-4 w-4" />
            Add Toolroom
          </Button>
          {selectedToolroom && (
            <Button onClick={() => setShowAddLocationModal(true)}>
              <Plus className="h-4 w-4" />
              Add Location
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Toolrooms List */}
        <Card>
          <CardHeader>
            <h2 className="font-semibold flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Toolrooms
            </h2>
          </CardHeader>
          <CardBody className="p-0">
            {toolroomsLoading ? (
              <div className="text-center py-8">
                <div className="spinner h-6 w-6 mx-auto" />
              </div>
            ) : toolrooms?.length === 0 ? (
              <div className="text-center py-8 text-foreground-muted px-4">
                <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No toolrooms yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {toolrooms?.map((toolroom) => (
                  <div
                    key={toolroom.id}
                    className={`p-4 cursor-pointer hover:bg-surface-hover transition-colors ${
                      selectedToolroom === toolroom.id ? 'bg-surface-hover' : ''
                    }`}
                    onClick={() => setSelectedToolroom(toolroom.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Building2 className="h-5 w-5 text-primary" />
                        <div>
                          <p className="font-medium">{toolroom.name}</p>
                          <p className="text-xs text-foreground-muted">
                            {toolroom.location_count} locations
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {toolroom.is_main === 1 && (
                          <Badge variant="primary" className="mr-2">Main</Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingToolroom(toolroom);
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (toolroom.location_count > 0) {
                              alert('Cannot delete toolroom with locations');
                              return;
                            }
                            if (confirm('Delete this toolroom?')) {
                              deleteToolroomMutation.mutate(toolroom.id);
                            }
                          }}
                          disabled={toolroom.location_count > 0}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <ChevronRight className="h-4 w-4 text-foreground-muted" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Locations List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <h2 className="font-semibold flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Storage Locations
            </h2>
          </CardHeader>
          <CardBody>
            {!selectedToolroom ? (
              <div className="text-center py-12 text-foreground-muted">
                <MapPin className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Select a toolroom to view locations</p>
              </div>
            ) : locationsLoading ? (
              <div className="text-center py-12">
                <div className="spinner h-8 w-8 mx-auto" />
              </div>
            ) : topLevelLocations.length === 0 ? (
              <div className="text-center py-12 text-foreground-muted">
                <FolderOpen className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>No locations in this toolroom</p>
                <p className="text-sm">Add cabinets, shelves, or drawers</p>
              </div>
            ) : (
              <div className="space-y-2">
                {topLevelLocations.map((location) => (
                  <LocationItem
                    key={location.id}
                    location={location}
                    children={getChildren(location.id)}
                    getChildren={getChildren}
                    onEdit={setEditingLocation}
                    onDelete={(id) => {
                      if (confirm('Delete this location?')) {
                        deleteLocationMutation.mutate(id);
                      }
                    }}
                    onPrintLabel={handlePrintLabel}
                    level={0}
                  />
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Add Toolroom Modal */}
      <ToolroomModal
        isOpen={showAddToolroomModal}
        onClose={() => setShowAddToolroomModal(false)}
        onSuccess={() => {
          setShowAddToolroomModal(false);
          queryClient.invalidateQueries({ queryKey: ['toolrooms'] });
        }}
      />

      {/* Edit Toolroom Modal */}
      {editingToolroom && (
        <ToolroomModal
          toolroom={editingToolroom}
          isOpen={true}
          onClose={() => setEditingToolroom(null)}
          onSuccess={() => {
            setEditingToolroom(null);
            queryClient.invalidateQueries({ queryKey: ['toolrooms'] });
          }}
        />
      )}

      {/* Add Location Modal */}
      {selectedToolroom && (
        <LocationModal
          isOpen={showAddLocationModal}
          toolroomId={selectedToolroom}
          locations={locations || []}
          onClose={() => setShowAddLocationModal(false)}
          onSuccess={() => {
            setShowAddLocationModal(false);
            queryClient.invalidateQueries({ queryKey: ['locations', selectedToolroom] });
            queryClient.invalidateQueries({ queryKey: ['toolrooms'] });
          }}
        />
      )}

      {/* Edit Location Modal */}
      {editingLocation && selectedToolroom && (
        <LocationModal
          location={editingLocation}
          toolroomId={selectedToolroom}
          locations={locations || []}
          isOpen={true}
          onClose={() => setEditingLocation(null)}
          onSuccess={() => {
            setEditingLocation(null);
            queryClient.invalidateQueries({ queryKey: ['locations', selectedToolroom] });
            queryClient.invalidateQueries({ queryKey: ['toolrooms'] });
          }}
        />
      )}
    </div>
  );
}

// Location Item Component
interface LocationItemProps {
  location: StorageLocation;
  children: StorageLocation[];
  getChildren: (id: string) => StorageLocation[];
  onEdit: (location: StorageLocation) => void;
  onDelete: (id: string) => void;
  onPrintLabel: (location: StorageLocation) => void;
  level: number;
}

function LocationItem({ location, children, getChildren, onEdit, onDelete, onPrintLabel, level }: LocationItemProps) {
  const [expanded, setExpanded] = useState(true);

  const typeColors: Record<string, string> = {
    cabinet: 'bg-blue-500/10 text-blue-500',
    drawer: 'bg-green-500/10 text-green-500',
    shelf: 'bg-purple-500/10 text-purple-500',
    bin: 'bg-orange-500/10 text-orange-500',
    slot: 'bg-gray-500/10 text-gray-500',
  };

  return (
    <div className={level > 0 ? 'ml-6 border-l border-border pl-4' : ''}>
      <div className="flex items-center justify-between p-2 rounded-lg hover:bg-surface-hover group">
        <div className="flex items-center gap-3">
          {children.length > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1 hover:bg-surface-hover rounded"
            >
              <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </button>
          )}
          {children.length === 0 && <div className="w-6" />}
          <div className={`px-2 py-1 rounded text-xs font-medium ${typeColors[location.location_type] || 'bg-gray-500/10'}`}>
            {location.location_type}
          </div>
          <div>
            <p className="font-medium">{location.name}</p>
            {location.barcode && (
              <p className="text-xs text-foreground-muted font-mono">{location.barcode}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="icon" onClick={() => onEdit(location)} title="Edit location">
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onPrintLabel(location)} title="Print label">
            <Printer className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (children.length > 0) {
                alert('Cannot delete location with child locations');
                return;
              }
              onDelete(location.id);
            }}
            disabled={children.length > 0}
            title="Delete location"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {expanded && children.length > 0 && (
        <div className="mt-1">
          {children.map((child) => (
            <LocationItem
              key={child.id}
              location={child}
              children={getChildren(child.id)}
              getChildren={getChildren}
              onEdit={onEdit}
              onDelete={onDelete}
              onPrintLabel={onPrintLabel}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Toolroom Modal
interface ToolroomModalProps {
  toolroom?: Toolroom;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function ToolroomModal({ toolroom, isOpen, onClose, onSuccess }: ToolroomModalProps) {
  const [formData, setFormData] = useState({
    name: toolroom?.name || '',
    description: toolroom?.description || '',
    isMain: toolroom?.is_main === 1,
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (toolroom) {
        const res = await api.put(`/locations/toolrooms/${toolroom.id}`, {
          name: data.name,
          description: data.description || undefined,
          isMain: data.isMain,
        });
        if (!res.success) throw new Error(res.error || 'Failed to update');
        return res.data;
      } else {
        const res = await api.post('/locations/toolrooms', {
          name: data.name,
          description: data.description || undefined,
          isMain: data.isMain,
        });
        if (!res.success) throw new Error(res.error || 'Failed to create');
        return res.data;
      }
    },
    onSuccess: () => {
      setFormData({ name: '', description: '', isMain: false });
      setError('');
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Operation failed');
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={toolroom ? 'Edit Toolroom' : 'Add Toolroom'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate(formData)} loading={mutation.isPending}>
            {toolroom ? 'Save Changes' : 'Create Toolroom'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>
        )}

        <Input
          label="Name *"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Main Tool Crib, Secondary Storage"
        />

        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Optional description..."
            className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            rows={3}
          />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isMain}
            onChange={(e) => setFormData({ ...formData, isMain: e.target.checked })}
            className="rounded border-border"
          />
          <span className="text-sm">Main toolroom (default location)</span>
        </label>
      </div>
    </Modal>
  );
}

// Location Modal
interface LocationModalProps {
  location?: StorageLocation;
  toolroomId: string;
  locations: StorageLocation[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function LocationModal({ location, toolroomId, locations, isOpen, onClose, onSuccess }: LocationModalProps) {
  const [formData, setFormData] = useState({
    name: location?.name || '',
    locationType: location?.location_type || 'cabinet',
    parentId: location?.parent_id || '',
    barcode: location?.barcode || '',
    capacity: location?.capacity?.toString() || '',
    description: location?.description || '',
  });
  const [error, setError] = useState('');

  const typeOptions = [
    { value: 'cabinet', label: 'Cabinet' },
    { value: 'drawer', label: 'Drawer' },
    { value: 'shelf', label: 'Shelf' },
    { value: 'bin', label: 'Bin' },
    { value: 'slot', label: 'Slot' },
  ];

  const parentOptions = [
    { value: '', label: 'None (top level)' },
    ...locations
      .filter(l => l.id !== location?.id)
      .map(l => ({ value: l.id, label: l.name })),
  ];

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (location) {
        const res = await api.put(`/locations/storage/${location.id}`, {
          name: data.name,
          locationType: data.locationType,
          parentId: data.parentId || undefined,
          barcode: data.barcode || undefined,
          capacity: data.capacity ? parseInt(data.capacity) : undefined,
          description: data.description || undefined,
        });
        if (!res.success) throw new Error(res.error || 'Failed to update');
        return res.data;
      } else {
        const res = await api.post('/locations/storage', {
          toolroomId,
          name: data.name,
          locationType: data.locationType,
          parentId: data.parentId || undefined,
          barcode: data.barcode || undefined,
          capacity: data.capacity ? parseInt(data.capacity) : undefined,
          description: data.description || undefined,
        });
        if (!res.success) throw new Error(res.error || 'Failed to create');
        return res.data;
      }
    },
    onSuccess: () => {
      setFormData({ name: '', locationType: 'cabinet', parentId: '', barcode: '', capacity: '', description: '' });
      setError('');
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Operation failed');
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={location ? 'Edit Location' : 'Add Location'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate(formData)} loading={mutation.isPending}>
            {location ? 'Save Changes' : 'Create Location'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>
        )}

        <Input
          label="Name *"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Cabinet A, Drawer 1"
        />

        <Select
          label="Type *"
          options={typeOptions}
          value={formData.locationType}
          onChange={(e) => setFormData({ ...formData, locationType: e.target.value })}
        />

        <Select
          label="Parent Location"
          options={parentOptions}
          value={formData.parentId}
          onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
        />

        <Input
          label="Barcode"
          value={formData.barcode}
          onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
          placeholder="e.g., CAB-A-001"
        />

        <Input
          label="Capacity"
          type="number"
          value={formData.capacity}
          onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
          placeholder="Optional max items"
        />

        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Optional description..."
            className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            rows={2}
          />
        </div>
      </div>
    </Modal>
  );
}
